import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Avatar,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PeopleIcon from "@mui/icons-material/People";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { usePDFGenerator } from "../../../hooks/usePDFGenerator";

/**
 * Header component for client meal plan viewer
 * Displays meal plan title, description, key stats, and PDF export options
 */
const ClientMealPlanHeader = ({
  mealPlan,
  organizationBranding,
  recipes = [],
  weeklySchedule = {},
}) => {
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState(null);

  // PDF generation hook
  const { generateMealPlanCalendarPDF, generateMealPlanFullPDF, isGenerating } =
    usePDFGenerator();

  if (!mealPlan) return null;

  const totalRecipes = mealPlan.selectedRecipes?.length || 0;

  // Handle PDF generation
  const handleCalendarPDF = async () => {
    try {
      await generateMealPlanCalendarPDF(
        mealPlan,
        recipes,
        weeklySchedule,
        organizationBranding,
      );
    } catch (error) {
      console.error("Error generating calendar PDF:", error);
      alert("Failed to generate calendar PDF. Please try again.");
    }
  };

  const handleFullPDF = async () => {
    try {
      await generateMealPlanFullPDF(
        mealPlan,
        recipes,
        weeklySchedule,
        organizationBranding,
      );
    } catch (error) {
      console.error("Error generating full PDF:", error);
      alert("Failed to generate full PDF. Please try again.");
    }
  };

  return (
    <Card
      sx={{
        mb: 4,
        background: `linear-gradient(135deg, ${organizationBranding?.primary || "#427674"} 0%, ${organizationBranding?.primaryLight || "#a9e3d7"} 100%)`,
        color: "white",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          width: "200px",
          height: "200px",
          background: "rgba(255,255,255,0.1)",
          borderRadius: "50%",
          transform: "translate(50%, -50%)",
        },
      }}
    >
      <CardContent sx={{ p: 4, position: "relative", zIndex: 1 }}>
        {/* PDF Export Button */}
        <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}>
          <Button
            startIcon={<PictureAsPdfIcon />}
            endIcon={<ArrowDropDownIcon />}
            variant="contained"
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
              textTransform: "none",
              pointerEvents: "auto",
              cursor: "pointer",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.3)",
              },
              "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                pointerEvents: "none",
              },
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setPdfMenuAnchor(e.currentTarget);
            }}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Export PDF"}
          </Button>

          {/* PDF Menu */}
          <Menu
            anchorEl={pdfMenuAnchor}
            open={Boolean(pdfMenuAnchor)}
            onClose={() => setPdfMenuAnchor(null)}
            PaperProps={{
              sx: { minWidth: 220 },
            }}
          >
            <MenuItem
              onClick={() => {
                handleCalendarPDF();
                setPdfMenuAnchor(null);
              }}
              disabled={isGenerating}
            >
              <ListItemIcon>
                <CalendarTodayIcon fontSize="small" />
              </ListItemIcon>
              Calendar View PDF
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                (Summary)
              </Typography>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleFullPDF();
                setPdfMenuAnchor(null);
              }}
              disabled={isGenerating}
            >
              <ListItemIcon>
                <MenuBookIcon fontSize="small" />
              </ListItemIcon>
              Full Meal Plan PDF
              <Typography
                variant="caption"
                sx={{ ml: 1, color: "text.secondary" }}
              >
                (With Recipes)
              </Typography>
            </MenuItem>
          </Menu>
        </Box>

        {/* Organization branding */}
        {organizationBranding?.organizationName && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
              Created by {organizationBranding.organizationName}
            </Typography>
          </Box>
        )}

        {/* Meal plan title */}
        <Typography variant="h1" sx={{ mb: 2, fontWeight: 700 }}>
          {mealPlan.name}
        </Typography>

        {/* Description */}
        {mealPlan.description && (
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              opacity: 0.95,
              fontWeight: 400,
              lineHeight: 1.5,
              maxWidth: "800px",
            }}
          >
            {mealPlan.description}
          </Typography>
        )}

        {/* Stats */}
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                width: 32,
                height: 32,
              }}
            >
              <RestaurantMenuIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1 }}>
                Recipes
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
                {totalRecipes}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                width: 32,
                height: 32,
              }}
            >
              <AccessTimeIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1 }}>
                Duration
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
                7 Days
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                width: 32,
                height: 32,
              }}
            >
              <PeopleIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1 }}>
                Target Calories
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1 }}>
                {mealPlan.macroTargets?.calories || "2000"}
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* Dietary info */}
        {mealPlan.dietaryRestrictions &&
          mealPlan.dietaryRestrictions.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 3 }}
              flexWrap="wrap"
              useFlexGap
            >
              {mealPlan.dietaryRestrictions.map((restriction, index) => (
                <Chip
                  key={index}
                  label={restriction}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 500,
                    "& .MuiChip-label": {
                      px: 2,
                    },
                  }}
                />
              ))}
            </Stack>
          )}
      </CardContent>
    </Card>
  );
};

export default ClientMealPlanHeader;
